import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { reportsAPI } from '../services/api';
import Button from '../components/UI/Button';
import { API_BASE_URL } from '../config/runtime';

interface ReportData {
  animals?: {
    summary: {
      total: number;
      available: number;
      adopted: number;
      reserved: number;
      hospital: number;
    };
    demographics: {
      bySize: Array<{ size: string; count: number }>;
      byGender: Array<{ gender: string; count: number }>;
      byStatus: Array<{ status: string; count: number }>;
    };
    health: {
      vaccination: Array<{ vaccination_status: string; count: number }>;
      neutering: Array<{ neutering_status: string; count: number }>;
    };
    recentArrivals: Array<{ name: string; size: string; gender: string; entryDate: string; status: string; age: string }>;
    insights: string[];
  };
  adoptions?: {
    summary: {
      total: number;
      pending: number;
      rejected: number;
      successRate: number;
    };
    trends: {
      byMonth: Array<{ month: string; month_name: string; count: number }>;
    };
    recent: Array<{ animalName: string; adopterFirstName: string; adopterLastName: string; applicationDate: string }>;
    topReasons: Array<{ adoptionReason: string; count: number }>;
    insights: string[];
  };
  volunteers?: {
    summary: {
      total: number;
      active: number;
      pending: number;
      inactive: number;
      totalHours: number;
      averageHours: number;
    };
    demographics: {
      byStatus: Array<{ status: string; count: number }>;
      joinTrends: Array<{ month: string; month_name: string; count: number }>;
    };
    performance: {
      topVolunteers: Array<{ firstName: string; lastName: string; totalHours: number; joinDate: string; status: string }>;
      recentActivities: Array<{ activity: string; date: string; hours: number; description: string; volunteerName: string }>;
    };
    insights: string[];
  };
  financial?: {
    summary: {
      totalInvestment: number;
      medicalExpenses: number;
      monthlyExpenses: number;
      totalExpenses: number;
    };
    trends: {
      expensesByMonth: Array<{ month: string; month_name: string; expenses: number }>;
    };
    insights: string[];
  };
  overview?: {
    totalAnimals: number;
    availableAnimals: number;
    totalVolunteers: number;
    activeVolunteers: number;
    totalAdoptions: number;
    pendingAdoptions: number;
    totalHours: number;
  };
  insights?: string[];
}

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<'overview' | 'adoptions' | 'volunteers' | 'animals'>('overview');
  const [error, setError] = useState<string | null>(null);

  const loadReportData = async (reportType: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      let reportResponse;
      if (reportType === 'overview') {
        reportResponse = await reportsAPI.generateReport({
          reportType: 'comprehensive',
          reportName: 'Raport complet',
          reportPeriod: 'current'
        });
      } else {
        reportResponse = await reportsAPI.generateReport({
          reportType: reportType,
          reportName: `Raport ${reportType}`,
          reportPeriod: 'current'
        });
      }
      
      if (reportResponse.success) {
        const data = reportResponse.data.reportData;
        setReportData(data);
      } else {
        const errorMsg = reportResponse.error || reportResponse.message || 'Failed to load report data';
        console.error('API returned error:', errorMsg);
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      setError(`Failed to load report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReportData(selectedReport);
  }, [selectedReport]);

  const exportReport = async (format: 'excel' | 'pdf') => {
    if (!reportData) {
      alert('Nu sunt date disponibile pentru export');
      return;
    }

    try {
      const reportTitle = getReportTitle();
      
      // Extract only the data for the current selected report section
      let sectionData = {};
      if (selectedReport === 'overview') {
        sectionData = {
          overview: reportData.overview,
          insights: reportData.insights,
          // Include summary data from all sections for overview
          animals: reportData.animals,
          volunteers: reportData.volunteers,
          adoptions: reportData.adoptions,
          financial: reportData.financial
        };
      } else {
        // For specific reports, only include that section's data
        sectionData = {
          [selectedReport]: reportData[selectedReport],
          insights: reportData[selectedReport]?.insights || reportData.insights
        };
      }

      const exportData = {
        reportType: selectedReport,
        format,
        data: sectionData,
        metadata: {
          title: reportTitle,
          generatedAt: new Date().toISOString(),
          shelterName: 'Adăpostul de Animale ODAI',
          reportDate: new Date().toLocaleDateString('ro-RO'),
          logo: '/logo-blue.svg'
        }
      };

      // Get authentication headers
      const token = localStorage.getItem('shelter-token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/reports/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify(exportData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportTitle.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Show success message
        alert(`Raportul ${format.toUpperCase()} pentru "${reportTitle}" a fost descărcat cu succes!`);
      } else {
        const errorText = await response.text();
        let errorMessage = 'Eroare necunoscută';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        alert(`Eroare la exportul raportului: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Eroare la exportul raportului. Vă rugăm să încercați din nou.');
    }
  };

  const renderInsights = (insights: string[]) => (
    <div className="bg-primary-100 rounded-lg p-4 mb-6">
                      <h3 className="text-lg font-medium text-primary-dark mb-3">Perspective și insights</h3>
      <ul className="space-y-2">
        {insights.map((insight, index) => (
          <li key={index} className="text-primary-dark flex items-start">
            <span className="text-primary mr-2">•</span>
            {insight}
          </li>
        ))}
      </ul>
    </div>
  );

  const getReportTitle = () => {
    switch (selectedReport) {
      case 'overview':
        return 'Privire de ansamblu';
      case 'animals':
        return 'Raport animale';
      case 'adoptions':
        return 'Raport adopții';
      case 'volunteers':
        return 'Raport voluntari';
      default:
        return 'Raport';
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Eroare la încărcarea raportului
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rapoarte și analize</h1>
            <p className="text-gray-600">Generează rapoarte complete și analize pentru adăpost</p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => exportReport('excel')}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </Button>
            <Button variant="outline" onClick={() => exportReport('pdf')}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Export PDF
            </Button>
          </div>
        </div>

        {/* Report Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selectează tipul de raport
              </label>
              <select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="overview">Privire de ansamblu</option>
                <option value="animals">Raport animale</option>
                <option value="adoptions">Raport adopții</option>
                <option value="volunteers">Raport voluntari</option>
              </select>
            </div>
          </div>
        </div>

        {/* Report Content */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">{getReportTitle()}</h2>
          


          {/* Overview Report */}
          {selectedReport === 'overview' && reportData && reportData.overview && (
            <div className="space-y-6">
              {/* Insights */}
              {reportData.insights && renderInsights(reportData.insights)}
              
              {/* Overview Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total animale</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.overview?.totalAnimals || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Disponibile</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.overview?.availableAnimals || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Voluntari activi</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.overview?.activeVolunteers || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Adopții totale</p>
                      <p className="text-2xl font-bold text-gray-900">{reportData.animals?.summary?.adopted || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed sections */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Animals Summary */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Rezumat animale</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total animale</span>
                      <span className="text-sm font-medium text-gray-900">{reportData.animals?.summary?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Disponibile</span>
                      <span className="text-sm font-medium text-green-600">{reportData.animals?.summary?.available || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Adoptate</span>
                      <span className="text-sm font-medium text-primary">{reportData.animals?.summary?.adopted || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">La spital</span>
                      <span className="text-sm font-medium text-red-600">{reportData.animals?.summary?.hospital || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Investiții animale</span>
                      <span className="text-sm font-medium text-primary">{(reportData.financial?.summary?.totalInvestment || 0).toLocaleString('ro-RO')} RON</span>
                    </div>
                  </div>
                </div>

                {/* Volunteers Summary */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Rezumat voluntari</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total voluntari</span>
                      <span className="text-sm font-medium text-gray-900">{reportData.volunteers?.summary?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Voluntari activi</span>
                      <span className="text-sm font-medium text-green-600">{reportData.volunteers?.summary?.active || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Ore totale</span>
                      <span className="text-sm font-medium text-primary">{Math.round(reportData.volunteers?.summary?.totalHours || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Ore medii</span>
                      <span className="text-sm font-medium text-gray-900">{Math.round(reportData.volunteers?.summary?.averageHours || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Animals Report */}
          {selectedReport === 'animals' && reportData && (reportData.animals || reportData.summary) && (
            <div className="space-y-6">
              {/* Insights */}
              {(reportData.animals?.insights || reportData.insights) && renderInsights(reportData.animals?.insights || reportData.insights)}
              
              {/* Animal Stats */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Total</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.animals?.summary || reportData.summary)?.total}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Disponibile</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.animals?.summary || reportData.summary)?.available}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Adoptate</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.animals?.summary || reportData.summary)?.adopted}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">Rezervate</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.animals?.summary || reportData.summary)?.reserved}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 14c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c5.51 0 10-4.48 10-10S17.51 2 12 2z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600">La spital</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.animals?.summary || reportData.summary)?.hospital}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuție pe mărime</h3>
                  <div className="space-y-3">
                    {(reportData.animals?.demographics || reportData.demographics)?.bySize?.map((size: { size: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{size.size}</span>
                        <span className="text-sm font-medium text-gray-900">{size.count} animale</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuție pe gen</h3>
                  <div className="space-y-3">
                    {(reportData.animals?.demographics || reportData.demographics)?.byGender?.map((gender: { gender: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{gender.gender === 'male' ? 'Masculin' : 'Feminin'}</span>
                        <span className="text-sm font-medium text-gray-900">{gender.count} animale</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuție pe status</h3>
                  <div className="space-y-3">
                    {(reportData.animals?.demographics || reportData.demographics)?.byStatus?.map((status: { status: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{status.status}</span>
                        <span className="text-sm font-medium text-gray-900">{status.count} animale</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Health Statistics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Status vaccinare</h3>
                  <div className="space-y-3">
                    {(reportData.animals?.health || reportData.health)?.vaccination?.map((vac, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{vac.vaccination_status === 'vaccinated' ? 'Vaccinati' : 'Nevaccinati'}</span>
                        <span className="text-sm font-medium text-gray-900">{vac.count} animale</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Status sterilizare</h3>
                  <div className="space-y-3">
                    {(reportData.animals?.health || reportData.health)?.neutering?.map((neut, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{neut.neutering_status === 'neutered' ? 'Sterilizati' : 'Nesterilizati'}</span>
                        <span className="text-sm font-medium text-gray-900">{neut.count} animale</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Arrivals */}
              {((reportData.animals?.recentArrivals || reportData.recentArrivals)?.length > 0) && (
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Sosiri recente (ultimele 30 zile)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nume</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vârstă</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gen</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mărime</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data sosirii</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(reportData.animals?.recentArrivals || reportData.recentArrivals)?.map((animal, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{animal.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{animal.age}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{animal.gender === 'male' ? 'M' : 'F'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{animal.size}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(animal.entryDate).toLocaleDateString('ro-RO')}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                animal.status === 'available' ? 'bg-green-100 text-green-800' :
                                animal.status === 'adopted' ? 'bg-primary-100 text-primary-dark' :
                                animal.status === 'hospital' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {animal.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Adoptions Report */}
          {selectedReport === 'adoptions' && reportData && (reportData.adoptions || reportData.summary) && (
            <div className="space-y-6">
              {/* Insights */}
              {(reportData.adoptions?.insights || reportData.insights) && renderInsights(reportData.adoptions?.insights || reportData.insights)}
              
              {/* Revenue Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Tendințe lunare</h3>
                  <div className="space-y-3">
                    {(reportData.adoptions?.trends || reportData.trends)?.byMonth?.slice(0, 3).map((month: { month: string; month_name: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{month.month_name}</span>
                        <span className="text-sm font-medium text-gray-900">{month.count} adopții</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Adoptions */}
              {((reportData.adoptions?.recent || reportData.recent)?.length > 0) && (
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Adopții recente</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animal</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adoptator</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(reportData.adoptions?.recent || reportData.recent)?.map((adoption, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{adoption.animalName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adoption.adopterFirstName} {adoption.adopterLastName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(adoption.applicationDate).toLocaleDateString('ro-RO')}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Fără taxă
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Volunteers Report */}
          {selectedReport === 'volunteers' && reportData && (reportData.volunteers || reportData.summary) && (
            <div className="space-y-6">
              {/* Insights */}
              {(reportData.volunteers?.insights || reportData.insights) && renderInsights(reportData.volunteers?.insights || reportData.insights)}
              
              {/* Volunteer Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total voluntari</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.volunteers?.summary || reportData.summary)?.total}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Voluntari activi</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.volunteers?.summary || reportData.summary)?.active}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Ore totale</p>
                      <p className="text-2xl font-bold text-gray-900">{Math.round((reportData.volunteers?.summary || reportData.summary)?.totalHours || 0)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Ore medii</p>
                      <p className="text-2xl font-bold text-gray-900">{(reportData.volunteers?.summary || reportData.summary)?.averageHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status and Trends */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuție pe status</h3>
                  <div className="space-y-3">
                    {(reportData.volunteers?.demographics || reportData.demographics)?.byStatus?.map((status: { status: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 capitalize">{status.status === 'active' ? 'Activ' : status.status === 'pending' ? 'În așteptare' : 'Inactiv'}</span>
                        <span className="text-sm font-medium text-gray-900">{status.count} voluntari</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Tendințe înscrieri</h3>
                  <div className="space-y-3">
                    {(reportData.volunteers?.demographics || reportData.demographics)?.joinTrends?.map((trend: { month: string; month_name: string; count: number }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{trend.month_name}</span>
                        <span className="text-sm font-medium text-gray-900">{trend.count} voluntari</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Volunteers */}
              {(reportData.volunteers?.performance || reportData.performance)?.topVolunteers && (
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Top voluntari</h3>
                  <div className="space-y-3">
                    {(reportData.volunteers?.performance || reportData.performance)?.topVolunteers?.map((volunteer: { firstName: string; lastName: string; totalHours: number; joinDate: string; status: string }, index: number) => (
                      <div key={index} className="flex justify-between items-center">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{volunteer.firstName} {volunteer.lastName}</span>
                          <span className="text-sm text-gray-500 ml-2">({volunteer.status})</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{Math.round(volunteer.totalHours)} ore</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financial Report */}
          {selectedReport === 'financial' && reportData && (reportData.financial || reportData.summary) && (
            <div className="space-y-6">
              {/* Insights */}
              {(reportData.financial?.insights || reportData.insights) && renderInsights(reportData.financial?.insights || reportData.insights)}
              
              {/* Animal Investments */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Investiții animale</p>
                      <p className="text-2xl font-bold text-gray-900">{((reportData.financial?.summary || reportData.summary)?.totalInvestment || 0).toLocaleString('ro-RO')} RON</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State for Individual Reports */}
          {!reportData && !isLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">
                <p>Nu sunt date disponibile pentru raportul selectat.</p>
                <p className="text-sm mt-2">Selectați un tip de raport din meniul de mai sus.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
